import { Component, Inject, ViewChild, AfterViewInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ApiService } from 'src/app/services/api.service';
import { PhoneMatchResult, BillMatchInfo, BillUpdateRequest, BillUpdateItem, PaymentHistoryItem } from 'src/app/model/phone-match.model';
import * as XLSX from 'xlsx';

@Component({
  selector: 'account-upload-modal',
  templateUrl: './account-upload.component.html',
  styleUrls: ['./account-upload.component.scss'],
})
export class AccountUploadDialog implements AfterViewInit {
  selectedFile: File | null = null;
  uploading = false;
  isDraggingOver = false;
  uploadResult: {
    totalRecords: number;
    savedCount: number;
    duplicateCount: number;
    invalidCount: number;
    duplicateTransferIds: string[];
  } | null = null;
  uploadStatus: 'success' | 'partial' | 'failure' | null = null;
  
  // Verification modal properties
  matchResults: PhoneMatchResult[] = [];
  matchResultsDataSource = new MatTableDataSource<PhoneMatchResult>([]);
  showVerificationModal: boolean = false;
  selectedBills: Set<string> = new Set();
  verificationMode: boolean = false;

  @ViewChild(MatSort) sort!: MatSort;
  updatingBills: boolean = false;

  // Discrepancy handling: rows (transferId_custId) whose discrepancy has been ignored so bills can be updated
  ignoredDiscrepancies: Set<string> = new Set();

  // Payment history (discrepancy investigation) modal
  showPaymentHistory: boolean = false;
  paymentHistoryLoading: boolean = false;
  paymentHistory: PaymentHistoryItem[] = [];
  paymentHistoryContext: { customerName: string | null; custId: number | null; phone: string; phoneSuffix: string; isDiscrepancy: boolean } | null = null;

  constructor(
    public dialogRef: MatDialogRef<AccountUploadDialog>,
    @Inject(MAT_DIALOG_DATA) public data: { employeeId: number; cycleStart: string; cycleEnd: string; matchResults?: PhoneMatchResult[]; verificationOnly?: boolean },
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {
    // If verificationOnly mode with pre-fetched results, show verification modal immediately
    if (data.verificationOnly && data.matchResults) {
      this.matchResults = data.matchResults;
      this.matchResultsDataSource.data = this.matchResults;
      this.showVerificationModal = true;
      this.verificationMode = true;
      this.selectedBills.clear();
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  ngAfterViewInit(): void {
    this.matchResultsDataSource.sort = this.sort;
    this.matchResultsDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'customer':
          return item.matchedCustomerName || '';
        case 'paymentStatus':
          return item.paymentStatus || '';
        case 'customerId':
          return item.matchedCustomerId || 0;
        default:
          const value = item[property as keyof PhoneMatchResult];
          if (typeof value === 'string') return value;
          if (typeof value === 'number') return value;
          return '';
      }
    };
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file: File): void {
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'csv'];
    if (validTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
      this.selectedFile = file;
    } else {
      this.snackBar.open('Please select a valid CSV or Excel file', 'Close', { duration: 3000 });
      this.selectedFile = null;
    }
  }

  onUpload(): void {
    if (!this.selectedFile) {
      this.snackBar.open('Please select a file to upload', 'Close', { duration: 3000 });
      return;
    }

    this.uploading = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('empId', this.data.employeeId.toString());
    formData.append('billingCycleStart', this.data.cycleStart);
    formData.append('billingCycleEnd', this.data.cycleEnd);
    
    const loggedInUser = window.localStorage.getItem('loggedUserName') || 'system';
    formData.append('createdBy', loggedInUser);

    this.apiService.uploadAccountData(formData).subscribe({
      next: (response) => {
        this.uploading = false;
        this.uploadResult = response;

        const total = response?.totalRecords ?? 0;
        const saved = response?.savedCount ?? 0;
        const skipped = (response?.duplicateCount ?? 0) + (response?.invalidCount ?? 0);

        if (saved > 0 && skipped === 0) {
          this.uploadStatus = 'success';
          this.snackBar.open(`Full success: all ${saved} record(s) uploaded`, 'Close', {
            duration: 4000,
            panelClass: ['snackbar-success']
          });
        } else if (saved > 0) {
          this.uploadStatus = 'partial';
          this.snackBar.open(`Partial success: ${saved} uploaded, ${skipped} skipped of ${total}`, 'Close', {
            duration: 5000,
            panelClass: ['snackbar-warning']
          });
        } else {
          this.uploadStatus = 'failure';
          this.snackBar.open(`Full failure: no records uploaded (${skipped} skipped of ${total})`, 'Close', {
            duration: 5000,
            panelClass: ['snackbar-error']
          });
        }
      },
      error: (error) => {
        this.uploading = false;
        this.uploadStatus = 'failure';
        this.snackBar.open('Error uploading file: ' + (error.message || 'Unknown error'), 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  done(): void {
    // Close with true if anything was saved so the list refreshes
    this.dialogRef.close((this.uploadResult?.savedCount ?? 0) > 0);
  }

  uploadAnother(): void {
    this.selectedFile = null;
    this.uploadResult = null;
    this.uploadStatus = null;
  }

  removeFile(): void {
    this.selectedFile = null;
  }

  toggleBillSelection(billId: number): void {
    const key = billId.toString();
    if (this.selectedBills.has(key)) {
      this.selectedBills.delete(key);
    } else {
      this.selectedBills.add(key);
    }
  }

  isBillSelected(billId: number): boolean {
    return this.selectedBills.has(billId.toString());
  }

  // Unique key per matched-customer row (a transfer can match multiple customers)
  rowKey(result: PhoneMatchResult): string {
    return `${result.transferId}_${result.matchedCustomerId}`;
  }

  isDiscrepancy(result: PhoneMatchResult): boolean {
    return result.matchedCustomerId !== null && result.paymentStatus === 'DISCREPANCY';
  }

  isIgnored(result: PhoneMatchResult): boolean {
    return this.ignoredDiscrepancies.has(this.rowKey(result));
  }

  toggleIgnoreDiscrepancy(result: PhoneMatchResult): void {
    const key = this.rowKey(result);
    if (this.ignoredDiscrepancies.has(key)) {
      this.ignoredDiscrepancies.delete(key);
      // De-select any bills that are now no longer allowed
      result.billsForCycle?.forEach(bill => this.selectedBills.delete(bill.billId.toString()));
    } else {
      this.ignoredDiscrepancies.add(key);
    }
  }

  // A row's bills can be updated only when a customer is matched AND
  // (there is no discrepancy OR the discrepancy has been explicitly ignored)
  isRowUpdatable(result: PhoneMatchResult): boolean {
    if (result.matchedCustomerId === null) {
      return false;
    }
    return !this.isDiscrepancy(result) || this.isIgnored(result);
  }

  get discrepancyRows(): PhoneMatchResult[] {
    return this.matchResults.filter(r => this.isDiscrepancy(r));
  }

  get allDiscrepanciesIgnored(): boolean {
    const rows = this.discrepancyRows;
    return rows.length > 0 && rows.every(r => this.isIgnored(r));
  }

  toggleIgnoreAll(): void {
    const rows = this.discrepancyRows;
    if (this.allDiscrepanciesIgnored) {
      rows.forEach(r => {
        this.ignoredDiscrepancies.delete(this.rowKey(r));
        r.billsForCycle?.forEach(bill => this.selectedBills.delete(bill.billId.toString()));
      });
    } else {
      rows.forEach(r => this.ignoredDiscrepancies.add(this.rowKey(r)));
    }
  }

  viewPaymentHistory(result: PhoneMatchResult): void {
    if (result.matchedCustomerId === null) {
      return;
    }
    this.paymentHistoryLoading = true;
    this.showPaymentHistory = true;
    this.paymentHistory = [];
    const phoneSuffix = result.phoneFrom && result.phoneFrom.length >= 7 ? result.phoneFrom.substring(3) : result.phoneFrom || '';
    this.paymentHistoryContext = {
      customerName: result.matchedCustomerName,
      custId: result.matchedCustomerId,
      phone: result.phoneFrom,
      phoneSuffix: phoneSuffix,
      isDiscrepancy: result.paymentStatus === 'DISCREPANCY'
    };

    this.apiService.getAccountUploadPaymentHistory(
      result.matchedCustomerId,
      result.phoneFrom,
      this.data.cycleEnd
    ).subscribe({
      next: (rows) => {
        this.paymentHistory = rows || [];
        this.paymentHistoryLoading = false;
      },
      error: (error) => {
        this.paymentHistoryLoading = false;
        this.snackBar.open('Error loading payment history: ' + (error.message || 'Unknown error'), 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  closePaymentHistory(): void {
    this.showPaymentHistory = false;
    this.paymentHistory = [];
    this.paymentHistoryContext = null;
  }

  toggleBulkSelect(): void {
    if (this.allSelected) {
      this.selectedBills.clear();
    } else {
      this.matchResults.forEach(result => {
        if (!this.isRowUpdatable(result)) {
          return;
        }
        result.billsForCycle?.forEach(bill => {
          this.selectedBills.add(bill.billId.toString());
        });
      });
    }
  }

  private get selectableBillCount(): number {
    let totalBills = 0;
    this.matchResults.forEach(result => {
      if (this.isRowUpdatable(result)) {
        totalBills += result.billsForCycle?.length || 0;
      }
    });
    return totalBills;
  }

  get allSelected(): boolean {
    const total = this.selectableBillCount;
    return total > 0 && this.selectedBills.size === total;
  }

  get matchedCount(): number {
    return this.matchResults.filter(r => r.matchedCustomerId !== null).length;
  }

  get unmatchedCount(): number {
    return this.matchResults.filter(r => r.matchedCustomerId === null).length;
  }

  exportUnmatchedCustomers(): void {
    const unmatched = this.matchResults.filter(r => r.matchedCustomerId === null);

    if (unmatched.length === 0) {
      this.snackBar.open('No unmatched customers to export', 'Close', { duration: 3000 });
      return;
    }

    const exportData = unmatched.map(result => ({
      'Transfer ID': result.transferId,
      'Phone From': result.phoneFrom,
      'Received Amount': result.receivedAmount,
      'Customer Phone': result.customerPhone,
      'Status': 'Customer Not Found'
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Unmatched Customers');
    XLSX.writeFile(wb, `unmatched_customers_${new Date().toISOString().split('T')[0]}.xlsx`);
  }

  confirmBillUpdates(): void {
    if (this.selectedBills.size === 0) {
      this.snackBar.open('Please select at least one bill to update', 'Close', { duration: 3000 });
      return;
    }

    this.updatingBills = true;
    const updates: BillUpdateItem[] = [];

    this.matchResults.forEach(result => {
      // Never update rows with an unresolved discrepancy or without a matched customer
      if (!this.isRowUpdatable(result)) {
        return;
      }
      result.billsForCycle?.forEach(bill => {
        if (this.selectedBills.has(bill.billId.toString())) {
          updates.push({
            billId: bill.billId,
            customerId: result.matchedCustomerId!,
            partialAmount: result.distributedAmount || result.receivedAmount,
            transferId: result.transferId,
            phoneFrom: result.phoneFrom
          });
        }
      });
    });

    if (updates.length === 0) {
      this.updatingBills = false;
      this.snackBar.open('No updatable bills selected (unresolved discrepancies are blocked)', 'Close', { duration: 4000 });
      return;
    }

    const request: BillUpdateRequest = {
      empId: this.data.employeeId,
      cycleStart: this.data.cycleStart,
      cycleEnd: this.data.cycleEnd,
      updates: updates
    };

    this.apiService.updateBillPartialAmounts(request).subscribe({
      next: (response) => {
        this.updatingBills = false;
        this.showVerificationModal = false;
        this.verificationMode = false;
        this.selectedBills.clear();
        
        const updated = response.updatedCount || 0;
        const skipped = response.skippedCount || 0;
        
        this.snackBar.open(
          `Updated ${updated} bill(s), ${skipped} skipped`,
          'Close',
          { duration: 5000, panelClass: ['snackbar-success'] }
        );
        
        // Close dialog to refresh parent
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.updatingBills = false;
        this.snackBar.open('Error updating bills: ' + (error.message || 'Unknown error'), 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  cancelVerification(): void {
    // If in verification-only mode, close the entire dialog to return to data page
    if (this.data.verificationOnly) {
      this.dialogRef.close(false);
      return;
    }
    
    // Otherwise, just hide the verification modal
    this.showVerificationModal = false;
    this.verificationMode = false;
    this.selectedBills.clear();
    this.ignoredDiscrepancies.clear();
    this.closePaymentHistory();
    this.matchResults = [];
    this.matchResultsDataSource.data = [];
  }

  formatPhoneForDisplay(phone: string): string {
    if (!phone) return '';
    // Format phone number for display (e.g., 252 613 804 901)
    if (phone.length === 12) {
      return `${phone.substring(0, 3)} ${phone.substring(3, 6)} ${phone.substring(6, 9)} ${phone.substring(9)}`;
    }
    return phone;
  }

  formatCurrency(amount: number): string {
    return '$' + (amount || 0).toFixed(2);
  }
}
